import { createApp } from 'vue';
import { toast } from 'vue3-toastify';
import API from '@/utils/API';

class Mails {

  /**
   * Envoi d'un email.
   * @param {string} templateName - Le nom du template.
   * @param {string} to - L'adresse email du destinataire.
   * @param {string} cc - L'adresse email en copie.
   * @param {string} bcc - L'adresse email en copie cachée.
   * @param {string} subject - L'objet de l'email.
   * @param {Object} props - Les props à passer au composant.
   * @returns {boolean} - `true` si l'email a été envoyé, `false` sinon.
   */
  async sendEmail(templateName, to, cc, bcc, subject, props = {}) {
    let html = await this.renderDynamicEmail(templateName, props)

    let r = await API.put('sendmail', {
      'A' : to,
      'CC': cc,
      'BCC': bcc,
      'Objet' : subject,
      'Message' : html
    });

    if (r?.Result !== 'ok') {
      toast.error(
        $root.$e("Une erreur s'est produite lors de l'envoi de l'email."),
        {
          closeButton: true,
          autoClose: true,
          closeOnClick: true,
          position: toast.POSITION.TOP_CENTER,
          theme: "dark",
        }
      );
      return false;
    }

    toast.success(
      $root.$e("L'email a été envoyé avec succès."),
      {
        closeButton: true,
        autoClose: true,
        closeOnClick: true,
        position: toast.POSITION.TOP_CENTER,
        theme: "dark",
      }
    );

    return true;
  }

  /**
   * Charge dynamiquement un composant Vue basé sur le nom du template.
   * @param {string} templateName - Le nom du template (chemin relatif dans `components`).
   * @returns {Promise<Object>} - Le composant Vue chargé.
   */
  async loadComponent(templateName) {
    const component = await import(`@/email/${templateName}.vue`);
    return component.default;
  }

  /**
   * Rendu dynamique d'un composant Vue en HTML.
   * @param {string} templateName - Le nom du template (chemin relatif dans `components`).
   * @param {Object} props - Les props à passer au composant.
   * @returns {Promise<string>} - Le HTML rendu sous forme de chaîne.
   */
  async renderDynamicEmail(templateName, props = {}) {
    const container = document.createElement('div');

    // Charger dynamiquement le composant
    const component = await this.loadComponent(templateName);

    // Crée et monte l'application Vue
    const app = createApp(component, props);
    app.mount(container);

    // Récupère le HTML généré
    const html = container.innerHTML;

    // Démonte l'application pour nettoyer les ressources
    app.unmount();

    return html;
  }
}

export default new Mails();
